import { BaseMap } from '../utils/map-data';
import { DEVICE_STATUS } from './Device';

export type Language = 'fr' | 'en';

export type GraphVariable =
  | 'mileage'
  | 'stops'
  | 'engine'
  | 'trip_time'
  | 'speed_avg'
  | 'eco_avg'
  | 'fuel_quantity_100_km'
  | 'fuel_cost_100_km'
  | 'CO2_100_km'
  | 'fuel_consumed';

export class DashboardCard {
  public constructor(
    public card?: string,
    public time?: number | string,
    public y?: GraphVariable,
    public group?: string,
    public period?: string,
    public uid_service?: number,
    public compare?: boolean
  ) {}
}
export class SchedulePreference {
  public workStart: string;
  public workEnd: string;
  public viewStart: string;
  public viewEnd: string;
  public constructor(scheduleBack?) {
    this.workStart = scheduleBack?.workStart || undefined;
    this.workEnd = scheduleBack?.workEnd || undefined;
    this.viewStart = scheduleBack?.viewStart || undefined;
    this.viewEnd = scheduleBack?.viewEnd || undefined;
  }
}

export class MapPreference {
  public basemap: BaseMap;
  public poiSize: 'none' | 'poiLayer' | 'poiLayerZoomed';
  public deviceInfo: string;
  public area: string;
  public eco: string;
  public poiFilter: string;
  public deviceFilter: string;
  public devicesTracking: boolean;
  public deviceMovingStatusFilter: string | undefined; // [] | null | (keyof typeof DEVICE_STATUS)[]
  public deviceTypeFilter: string | undefined; // [] | null | DeviceType[]
  public constructor(mapBack?) {
    this.basemap = mapBack?.basemap || undefined;
    this.poiSize = mapBack?.poiSize || undefined;
    this.deviceInfo = mapBack?.deviceInfo || undefined;
    this.area = mapBack?.area || undefined;
    this.eco = mapBack?.eco || undefined;
    this.poiFilter = mapBack?.poiFilter || undefined;
    this.deviceFilter = mapBack?.deviceFilter || undefined;
    this.deviceMovingStatusFilter = mapBack?.deviceMovingStatusFilter || undefined;
    this.devicesTracking = mapBack?.devicesTracking ? JSON.parse(mapBack?.devicesTracking) : undefined;
    this.deviceTypeFilter = mapBack?.deviceTypeFilter || undefined;
  }
}

export class DeviceCardPreference {
  public id: number;
  public data: string;
  public order: number;
  public constructor(deviceCardBack?) {
    this.id = deviceCardBack?.id || undefined;
    this.data = deviceCardBack?.data || undefined;
    this.order = deviceCardBack?.order || undefined;
  }
}

export class DevicePreference {
  public size: 'minimum' | 'maximum';
  public cards: DeviceCardPreference[];
  public constructor(deviceBack?) {
    this.size = deviceBack?.size || undefined;
    this.cards = deviceBack?.cards || [];
  }
}

export class TripTablePreference {
  public mode: string;
  public planning: string;
  public start: string;
  public stop: string;
  public duration: string;
  public mileage: string;
  public allmileage: string;
  public standby: string;
  public allstandby: string;
  public engine: string;
  public address: string;
  public nextstart: string;
  public speed: string;
  public device: string;
  public sensor: string;
  public stopTemp: string;
  public startTemp: string;
  public comment: string;
  public fuelconsumption: string;
  public eco: string;

  public constructor(tableBack?) {
    this.mode = tableBack?.mode || undefined;
    this.planning = tableBack?.planning || undefined;
    this.start = tableBack?.start || undefined;
    this.stop = tableBack?.stop || undefined;
    this.duration = tableBack?.duration || undefined;
    this.mileage = tableBack?.mileage || undefined;
    this.allmileage = tableBack?.allmileage || undefined;
    this.standby = tableBack?.standby || undefined;
    this.allstandby = tableBack?.allstandby || undefined;
    this.engine = tableBack?.engine || undefined;
    this.address = tableBack?.address || undefined;
    this.nextstart = tableBack?.nextstart || undefined;
    this.speed = tableBack?.speed || undefined;
    this.device = tableBack?.device || undefined;
    this.sensor = tableBack?.sensor || undefined;
    this.stopTemp = tableBack?.stopTemp || undefined;
    this.startTemp = tableBack?.startTemp || undefined;
    this.comment = tableBack?.comment || undefined;
    this.fuelconsumption = tableBack?.fuelconsumption || undefined;
    this.eco = tableBack?.eco || undefined;
  }
}

export class MissionPreference {
  public states: string[];
  public startHour: string;
  public endHour: string;
  public displayMissionToAffect: boolean;
  public missionToDriver: boolean;

  public constructor(missionBack?) {
    this.startHour = missionBack?.startHour || undefined;
    this.endHour = missionBack?.endHour || undefined;
    this.displayMissionToAffect = missionBack?.displayMissionToAffect || undefined;
    this.missionToDriver = missionBack?.missionToDriver || undefined;
  }
}
export class ConfigPreference {
  public speedUnit: string;
  public deviceView: string;
  public language: Language;
  public constructor(configBack?: ConfigPreference) {
    this.speedUnit = configBack?.speedUnit || undefined;
    this.deviceView = configBack?.deviceView || undefined;
    this.language = configBack?.language || 'fr';
  }
}
export class Preferences {
  public dashboard?: DashboardCard[];
  public schedule?: SchedulePreference;
  public map?: MapPreference;
  public triptable?: TripTablePreference;
  public mission?: MissionPreference;
  public config?: ConfigPreference;
  public device?: DevicePreference;
  public historyTableColumns?: HistoryColumnsPreference;
  public tableColumns?: TableColumnsPreference;

  public constructor(preferenceBack?) {
    this.dashboard = preferenceBack?.dashboard || cloneDefaultPreference('dashboard');
    this.schedule = preferenceBack?.schedule || cloneDefaultPreference('schedule');
    this.map = preferenceBack?.map || cloneDefaultPreference('map');
    this.triptable = preferenceBack?.triptable || cloneDefaultPreference('triptable');
    this.mission = preferenceBack?.mission || cloneDefaultPreference('mission');
    this.config = preferenceBack?.config || cloneDefaultPreference('config');
    this.device = preferenceBack?.device || cloneDefaultPreference('device');
    this.historyTableColumns =
      preferenceBack?.historyTableColumns || cloneDefaultPreference('historyTableColumns');
    this.tableColumns = preferenceBack?.tableColumns || cloneDefaultPreference('tableColumns');
  }
}

/**
 * Copie d'une valeur par défaut, à utiliser partout où `DefaultPreferences` alimente les
 * préférences d'un compte.
 *
 * `DefaultPreferences` est un objet de module, donc unique pour toute la session. Le distribuer
 * par référence fait écrire les préférences du compte dans les valeurs par défaut de
 * l'application, car `PreferenceService.setPreferences` écrit en place
 * (`this.preference[type][item] = …`). Les « valeurs par défaut » du reste de la session
 * deviennent alors celles de ce compte : les réinitialisations ne rétablissent plus rien, et un
 * second compte ouvert dans la même session hérite des filtres du premier.
 *
 * Copie JSON et non `structuredClone`, absent de l'environnement de test, et déjà l'idiome
 * employé ailleurs sur ces mêmes valeurs. Le garde sur `undefined` est nécessaire :
 * `JSON.parse(JSON.stringify(undefined))` lève une `SyntaxError`.
 */
export function cloneDefaultPreference<K extends keyof Preferences>(key: K): Preferences[K] {
  const defaultValue = DefaultPreferences[key];
  return defaultValue === undefined ? undefined : JSON.parse(JSON.stringify(defaultValue));
}
export class PreferenceData {
  public uid: number;
  public uid_responsable: number;
  public preference: Preferences;
  public app: string;

  public constructor(preferenceBack?) {
    this.uid = preferenceBack?.uid || undefined;
    this.uid_responsable = preferenceBack?.uid_responsable || undefined;
    this.preference = preferenceBack?.preference ? JSON.parse(preferenceBack?.preference) : undefined;
    this.app = preferenceBack?.app || undefined;
  }
}

export type HistoryColumnsPreference = string[];

/**
 * Colonnes affichées de chaque tableau de l'application : `key` identifie le tableau
 * (voir `TABLE_COLUMNS_PREFERENCE_KEYS`), `columns` liste les `dataKey` des colonnes cochées.
 *
 * Stocké en tableau et non en `Record` à dessein : `PreferenceService.setPreferences` ne
 * détecte correctement le changement (et donc ne sauvegarde) que pour les valeurs tableau.
 */
export type TableColumnsPreference = { key: string; columns: string[] }[];

export const deviceCardsDefault: DeviceCardPreference[] = [
  {
    id: 1,
    order: 0,
    data: 'device',
  },
  {
    id: 2,
    order: 1,
    data: 'position',
  },
  {
    id: 3,
    order: 2,
    data: 'devicegraph',
  },
  {
    id: 4,
    order: 3,
    data: 'maintenance',
  },
  {
    id: 5,
    order: 4,
    data: 'ecograph',
  },
  {
    id: 6,
    order: 5,
    data: 'cost',
  },
  {
    id: 7,
    order: 6,
    data: 'document',
  },
  {
    id: 8,
    order: 7,
    data: 'alarm',
  },
  {
    id: 9,
    order: 8,
    data: 'mission',
  },
  {
    id: 10,
    order: 9,
    data: 'detail',
  },
  {
    id: 11,
    order: 10,
    data: 'lockdevice',
  },
  {
    id: 12,
    order: 11,
    data: 'sharedevice',
  },
];

export const DefaultPreferences: Preferences = {
  dashboard: [
    {
      card: 'state',
      time: '0',
      uid_service: 0,
    },
    {
      card: 'map',
    },
    {
      card: 'poi',
    },
    {
      card: 'maintenance-state',
      uid_service: 0,
    },
    {
      card: 'maintenance',
    },
    {
      card: 'tripless',
      time: 1,
      uid_service: 0,
    },
    {
      card: 'news',
    },
    {
      card: 'private',
    },
    {
      card: 'lockeddevices',
    },
    {
      card: 'alarm-logs',
    },
    {
      period: 'M3',
      y: 'mileage',
      uid_service: null,
      group: 'device',
    },
    {
      period: 'M3',
      y: 'stops',
      uid_service: null,
      group: 'device',
    },
    {
      period: 'M3',
      y: 'engine',
      uid_service: null,
      group: 'device',
    },
    {
      period: 'M3',
      y: 'speed_avg',
      uid_service: null,
      group: 'device',
    },
    {
      period: 'M3',
      y: 'trip_time',
      uid_service: null,
      group: 'device',
    },
  ],
  schedule: {
    workStart: '08:00',
    workEnd: '20:00',
    viewStart: '08:00',
    viewEnd: '20:00',
  },
  map: {
    basemap: 'map',
    poiSize: 'poiLayer',
    deviceInfo: 'reg',
    area: 'true',
    eco: 'true',
    poiFilter: 'all',
    deviceFilter: 'all',
    deviceMovingStatusFilter: Object.keys(DEVICE_STATUS || {}) as any,
    devicesTracking: false,
    deviceTypeFilter: undefined,
  },
  triptable: {
    mode: 'basic',
    planning: 'true',
    start: 'true',
    stop: 'true',
    duration: 'true',
    mileage: 'true',
    allmileage: 'true',
    standby: 'true',
    allstandby: 'true',
    engine: 'false',
    address: 'true',
    nextstart: 'false',
    speed: 'false',
    device: 'true',
    sensor: 'true',
    stopTemp: 'false',
    startTemp: 'false',
    comment: 'false',
    fuelconsumption: 'false',
    eco: 'true',
  },
  mission: {
    states: ['S', 'B', 'E', 'R', 'L', 'A', 'T', 'F', 'C', 'K', 'Q', 'D', 'C_PENDING'],
    startHour: '08:00',
    endHour: '20:00',
    displayMissionToAffect: true,
    missionToDriver: false,
  },
  config: {
    speedUnit: 'speed',
    deviceView: 'registration',
    language: /^fr\b/.test(navigator.language) ? 'fr' : 'en',
  },
  device: {
    size: 'minimum',
    cards: deviceCardsDefault,
  },
  historyTableColumns: undefined,
  tableColumns: undefined,
};
